/**
 * What the ORDER region means to the surfaces that read it (PRD 4.2, BAK-002,
 * BAK-003, ALC-006, ALC-010).
 *
 * ## Why this file exists at all
 *
 * `assignment`, `bag` and `lifecycle` were three axes and are one serial
 * workflow. Everything a surface used to ask of them was a comparison against
 * one axis, and every one of those comparisons was quietly WIDER than it read:
 * the one that meant "a counter holds it" was true from the moment a counter
 * said yes until the bag reached the door, it was written at six call sites,
 * and at three of them the author meant "a counter has said yes and has not
 * started yet".
 *
 * Against `order` that question has to be answered out loud, because PREPARING
 * and READY are two states and not one - and the offer interval before them is
 * three (SCHEDULED, ACCEPTANCE_OPEN, ACCEPTANCE_CALLING). This file is where the
 * answer is given a NAME - once - so the next reader asks a question instead of
 * writing a list. It is the ORDER twin of `delivery-phase.ts`, and it follows
 * the same three rules: pure, exhaustive by construction (a twelfth
 * `OrderPhase` is a typecheck failure in every switch below, not a silent
 * `false`), and asked rather than copied.
 *
 * ## The refusal that rests
 *
 * `AWAITING_ASSIGNMENT` (#2155) is an order a counter refused - by a tap, or by
 * closing its day under one it had accepted - waiting for the every-minute
 * engine to offer it to another house or end it. Every question below answers
 * it as "nobody holds it, nothing is under way": it is not stopped, not under
 * an offer, not accepted, and nothing made for it is anybody's yet. A guest
 * whose order was handed on never sees it (decision 11): the guest's wire
 * presents that order at the stage it had reached.
 *
 * ## The two endings, and the one thing the region cannot say
 *
 * A CANCELLED or CLOSED order has stopped, and the region does not record where
 * it stopped. Both endings are answered from what could have reached them: a
 * cancellation could not happen once a courier held the bag
 * (`supportMayCancel`), so a cancelled order reads as nothing-made; an order
 * only closes from DELIVERED (engineering resolution 29), so a closed one reads
 * as collected. Both are stated at their switch arm rather than left to a
 * default.
 */

import type { OrderCancelledCause, OrderPhase } from '../enums';

/**
 * The order has stopped, one way or the other (old: `lifecycle !== 'OPEN'`).
 *
 * Not the domain's `orderEnded` (order-gates.ts), which counts DELIVERED as an
 * ending too: a delivered order has not STOPPED - it still closes - and the two
 * facts shared one name until the centralization audit (2026-10-02, C6).
 *
 * A type predicate on purpose: every caller that asks this asks it FIRST, and
 * the narrowing is what lets the switch after it be exhaustive over the phases
 * that can actually reach it, rather than carrying two arms nobody can hit.
 */
export function orderStopped(phase: OrderPhase): phase is 'CANCELLED' | 'CLOSED' {
  switch (phase) {
    case 'CANCELLED':
    case 'CLOSED':
      return true;
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
    case 'PREPARING':
    case 'READY':
    case 'AWAITING_ASSIGNMENT':
    case 'COLLECTED':
    case 'DELIVERED':
      return false;
  }
}

/**
 * THE ORDER IS SITTING UNDER AN OFFER (ALC-011): a counter has been offered it
 * and has not answered - before its acceptance opens, or while it may answer.
 * The wire twin of the domain's `isOfferState` / `offerTermsOf(...) !== null`,
 * and what the counter's `presentation.unanswered` says.
 */
export function orderUnderOffer(
  phase: OrderPhase,
): phase is 'SCHEDULED' | 'ACCEPTANCE_OPEN' | 'ACCEPTANCE_CALLING' {
  switch (phase) {
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
      return true;
    case 'CREATED':
    case 'PREPARING':
    case 'READY':
    // Refused: the counter answered, and nobody else has been asked yet.
    case 'AWAITING_ASSIGNMENT':
    case 'COLLECTED':
    case 'DELIVERED':
    case 'CANCELLED':
    case 'CLOSED':
      return false;
  }
}

/**
 * A COUNTER HOLDS THIS ORDER (BAK-003, BAK-043).
 *
 * Somebody said yes and has not been let go of. It spans the whole of the
 * counter's work and everything past it, because the acceptance is what none of
 * those states undoes.
 *
 * `BAKERY_ACCEPTED` lands the order in PREPARING, so "a counter said yes" and
 * "the goods are being handled" are one fact and this differs from
 * `orderPreparationStarted` at CLOSED alone. Both names stay, because the two
 * call sites mean different things and only the call site can say which.
 */
export function orderAcceptedByCounter(
  phase: OrderPhase,
): phase is 'PREPARING' | 'READY' | 'COLLECTED' | 'DELIVERED' {
  switch (phase) {
    case 'PREPARING':
    case 'READY':
    case 'COLLECTED':
    case 'DELIVERED':
      return true;
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
    // The counter that refused has let go, whatever it had said before.
    case 'AWAITING_ASSIGNMENT':
      return false;
    /*
     * The region carries the phase and no counter, so an ended order cannot say
     * whether one held it. Every caller in the tree asks `orderStopped` first and
     * never reaches this arm, and answering `true` here would say a counter is
     * holding an order that no longer exists.
     */
    case 'CANCELLED':
    case 'CLOSED':
      return false;
  }
}

/**
 * SOMETHING HAS BEEN DONE ABOUT THE GOODS - old: `bag !== 'NOT_STARTED'`.
 *
 * The line an operator's Reassign closes on (OPS-002) and the line the guest's
 * live view opens on: past it the order is work in progress rather than a
 * booking, and moving it elsewhere is a cancellation and a new order.
 */
export function orderPreparationStarted(
  phase: OrderPhase,
): phase is 'PREPARING' | 'READY' | 'COLLECTED' | 'DELIVERED' | 'CLOSED' {
  switch (phase) {
    case 'PREPARING':
    case 'READY':
    case 'COLLECTED':
    case 'DELIVERED':
      return true;
    // An order only closes from DELIVERED (resolution 29): it was made.
    case 'CLOSED':
      return true;
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
      return false;
    /*
     * Between two houses (#2155). A bag the closing house had begun is not the
     * next house's: its yes starts the preparation afresh.
     */
    case 'AWAITING_ASSIGNMENT':
      return false;
    // Nothing may be cancelled once a courier holds the bag, so the bag a
    // cancellation found was an unmade one.
    case 'CANCELLED':
      return false;
  }
}

/**
 * THE BAG IS FINISHED - old: `bag === 'READY' || bag === 'COLLECTED'`.
 *
 * One question, asked where a delivery fact and a bag fact meet and mean
 * opposite things: a rider standing at a counter is the system working when
 * the bag is made and the most urgent thing on the board when it is not
 * (`courierLineOf`, BAK-044).
 */
export function orderBagMade(
  phase: OrderPhase,
): phase is 'READY' | 'COLLECTED' | 'DELIVERED' | 'CLOSED' {
  return phase === 'READY' || orderBagCollected(phase);
}

/**
 * THE COURIER HAS THE BAG - old: `bag === 'COLLECTED'`.
 *
 * What ends the counter's hold on an order: More time, Cancel order and the
 * preparation countdown all stop here, whatever the courier does next
 * (BAK-040, BAK-043, BAK-004).
 */
export function orderBagCollected(
  phase: OrderPhase,
): phase is 'COLLECTED' | 'DELIVERED' | 'CLOSED' {
  switch (phase) {
    case 'COLLECTED':
    case 'DELIVERED':
      return true;
    // Closed from DELIVERED alone (resolution 29), which was collected.
    case 'CLOSED':
      return true;
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
    case 'PREPARING':
    case 'READY':
    // A rider who collects before the stand-down moves the order to COLLECTED.
    case 'AWAITING_ASSIGNMENT':
    // See `orderPreparationStarted`: a cancellation cannot reach a bag on a
    // scooter.
    case 'CANCELLED':
      return false;
  }
}

/**
 * NOBODY COULD MAKE THIS ONE (ALC-006, ALC-010, engineering resolution 25).
 *
 * The three states that used to hold this answer while the cascade ran -
 * REFUSED, NO_ANSWER, EXHAUSTED - are gone. A missed answer is cancelled or
 * re-offered in the SAME transaction (founder decision 8), and a refusal rests
 * on `AWAITING_ASSIGNMENT` only until the every-minute engine re-offers it or
 * ends it (#2155) - and until then another house may yet make it. So there is
 * no live question to ask: the answer is entirely on the ending, and
 * `cancelledCause` is what carries it.
 *
 * It is one function rather than a comparison at each call site for the reason
 * the whole projections directory exists: the guest page, the internal row and
 * anything that comes next must agree about which orders nobody could make,
 * and three copies of the pair below would not stay equal to each other.
 *
 * A cancellation the PREVIOUS release wrote carries no cause and answers false,
 * which is the safe direction: an ordinary cancellation rather than a claim
 * about the cascade that nothing on the row supports.
 */
const NEVER_ALLOCATED_CAUSES: readonly OrderCancelledCause[] = [
  'CASCADE_EXHAUSTED',
  'NO_ELIGIBLE_HOUSE',
];

export function orderNeverAllocated(lifecycle: {
  readonly order: OrderPhase;
  readonly cancelledCause?: OrderCancelledCause | null;
}): boolean {
  if (lifecycle.order !== 'CANCELLED') return false;
  const cause = lifecycle.cancelledCause;
  return cause != null && NEVER_ALLOCATED_CAUSES.includes(cause);
}
